import Head from 'next/head'
import getConfig from 'next/config'
import styled, { css } from 'styled-components'
import { useEffect } from 'react'
import { useRouter } from 'next/router'

import AffirmScript from 'shared/components/AffirmScript'
import AuthPanel from 'checkout/components/AuthPanel/AuthPanel'
import ContextErrorBanner from 'errors/components/ContextErrorBanner'
import ErrorModal from 'errors/components/ErrorModal'
import ExpressCheckoutPanel from 'checkout/components/ExpressCheckoutPanel/ExpressCheckoutPanel'
import OrderSummaryPanelGQL from 'checkout/components/OrderSummaryPanel/OrderSummaryPanel'
import PaymentMethodPanelGQL from 'checkout/components/PaymentMethodPanel/PaymentMethodPanel'
import PromotionPanelGQL from 'checkout/components/PromotionPanel/PromotionPanel'
import ShippingAddressPanelGQL from 'checkout/components/ShippingAddressPanel/ShippingAddressPanel'
import ShippingMethodPanelGQL from 'checkout/components/ShippingMethodPanel/ShippingMethodPanel'
import Spinner from 'shared/components/Spinner'
import { CheckoutQA } from 'shared/dataAttributes'
import { FeatureFlag } from 'shared/enums/FeatureFlag'
import { GuestCheckoutAuthPanel } from 'checkout/panels/GuestCheckoutAuthPanel'
import { OrderSummaryPanel } from 'checkout/panels/OrderSummary/OrderSummaryPanel'
import { PaymentMethodPanel } from 'checkout/panels/PaymentMethod/PaymentMethodPanel'
import { PromotionPanel } from 'checkout/panels/Promotion/PromotionPanel'
import { ShippingAddressPanel } from 'checkout/panels/ShippingAddress/ShippingAddressPanel'
import { ShippingMethodPanel } from 'checkout/panels/ShippingMethod/ShippingMethodPanel'
import { media } from 'shared/lib'
import { t } from 'localization'
import { useCheckoutContext } from 'checkout/containers/CheckoutContextProvider'
import { useFeatureFlag } from 'featureFlags/hooks/useFeatureFlag'
import { useUser } from 'shared/hooks/useUser'
import { useWindowSize } from 'shared/hooks/useWindowSize'
import { OrderSummaryOptionsMobile } from 'checkout/panels/OrderSummary/OrderSummaryOptionsMobile'
import { useElementOnScreen } from 'shared/hooks/useElementOnScreen'

const TP = 'checkout.containers.CheckoutPage'

const CheckoutPage = () => {
  const router = useRouter()
  const { isMobile } = useWindowSize()
  const guestCheckoutEnabled = useFeatureFlag(FeatureFlag.GUEST_CHECKOUT)
  const isAffirmLocaleValidationEnabled = useFeatureFlag(FeatureFlag.TEMP_WEB_FC_AFFIRM_WITH_LOCALE)
  const isUpgradeFtyEnabled = useFeatureFlag(FeatureFlag.TEMP_WEB_FC_PDP_HIDE_FASTEST_TO_YOU)
  const { displayCheckoutRTK, getPriceBreakdownRTK, errorModalMessage, orderLoading, order } =
    useCheckoutContext()
  const { publicRuntimeConfig } = getConfig()
  const { isAuthenticated } = useUser()

  const onSubmit = () => {
    // If user is returning from a payment vendor, redirect to the product page
    if (order?.product?.productTemplate?.slug) {
      return router.push(`/${order?.product?.productTemplate?.slug}`)
    }

    router.back()
  }

  useEffect(() => {
    if (displayCheckoutRTK) {
      getPriceBreakdownRTK()
    }
  }, [displayCheckoutRTK])

  const [containerRef, isVisible] = useElementOnScreen({
    rootMargin: '-40px',
    threshold: 1.0,
  })

  useEffect(() => {
    const stickyOrderSummary = document?.getElementById('sticky-order-summary')
    if (stickyOrderSummary && isVisible) {
      stickyOrderSummary.style.top = '-500px'
      stickyOrderSummary.style.transition = 'top 0.7s ease-out'
    }
    if (stickyOrderSummary && !isVisible) {
      stickyOrderSummary.style.top = '0px'
      stickyOrderSummary.style.transition = 'top 0.5s ease-in'
    }
  }, [isVisible])

  const checkoutPageBody = (() => {
    if (displayCheckoutRTK) {
      if (
        isUpgradeFtyEnabled &&
        isMobile &&
        order?.product?.productTemplate?.id &&
        order?.product?.productTemplate?.productCategory === 'shoes' &&
        order?.product?.variantShoeCondition === 'new_no_defects'
      ) {
        // RTK Checkout + Upgrade FTY Mobile
        return (
          <CheckoutPage.Layout $isMobile>
            <CheckoutPage.Left $isMobile>
              <ContextErrorBanner qaAttr={CheckoutQA.CheckoutErrorLoginTopForm} />
              <GuestCheckoutAuthPanel />
              <OrderSummaryOptionsMobile />
              <ShippingAddressPanel />
              <ShippingMethodPanel />
              <PaymentMethodPanel />
              {!order?.wantId && <PromotionPanel />}
            </CheckoutPage.Left>
            <CheckoutPage.Right $isMobile id="order-summary">
              <UnStickyOrderSummaryObserver ref={containerRef} />
              <CheckoutPage.OrderSummaryPanel wantId={order?.wantId} />
            </CheckoutPage.Right>
          </CheckoutPage.Layout>
        )
      }
      if (isUpgradeFtyEnabled && !isMobile) {
        // RTK Checkout + Upgrade FTY Desktop
        return (
          <CheckoutPage.Layout>
            <CheckoutPage.Left id="order-summary">
              <CheckoutPage.OrderSummaryPanel wantId={order?.wantId} />
            </CheckoutPage.Left>
            <CheckoutPage.Right>
              <ContextErrorBanner qaAttr={CheckoutQA.CheckoutErrorLoginTopForm} />
              <GuestCheckoutAuthPanel />
              <ShippingAddressPanel />
              <ShippingMethodPanel />
              <PaymentMethodPanel />
              {!order?.wantId && <PromotionPanel />}
            </CheckoutPage.Right>
          </CheckoutPage.Layout>
        )
      }
      // RTK Checkout + Lowest Price/FTY Selected from PDP
      return (
        <CheckoutPage.Layout>
          <CheckoutPage.Left>
            <ContextErrorBanner qaAttr={CheckoutQA.CheckoutErrorLoginTopForm} />
            <GuestCheckoutAuthPanel />
            <ShippingAddressPanel />
            <ShippingMethodPanel />
            <PaymentMethodPanel />
            {!order?.wantId && <PromotionPanel />}
          </CheckoutPage.Left>
          <CheckoutPage.Right id="order-summary">
            <CheckoutPage.OrderSummaryPanel wantId={order?.wantId} />
          </CheckoutPage.Right>
        </CheckoutPage.Layout>
      )
    }
    // GQL Checkout
    return (
      <CheckoutPage.Layout>
        <CheckoutPage.Left>
          <ContextErrorBanner qaAttr={CheckoutQA.CheckoutErrorLoginTopForm} />
          <AuthPanel />
          {!guestCheckoutEnabled && isAuthenticated && <ExpressCheckoutPanel />}
          <ShippingAddressPanelGQL />
          <ShippingMethodPanelGQL />
          <PaymentMethodPanelGQL />
          {!order?.wantId && <PromotionPanelGQL />}
        </CheckoutPage.Left>
        <CheckoutPage.Right id="order-summary">
          <CheckoutPage.OrderSummaryGQL />
        </CheckoutPage.Right>
      </CheckoutPage.Layout>
    )
  })()

  return (
    <>
      <Head>
        <title>{t(`${TP}.pageTitle`, 'Checkout | Flight Club')}</title>
        {publicRuntimeConfig?.afterPayJSUrl && (
          <script src={publicRuntimeConfig.afterPayJSUrl} async />
        )}
      </Head>
      {!isAffirmLocaleValidationEnabled && <AffirmScript />}
      <ErrorModal
        // Syntax error in the following line?
        message={errorModalMessage!}
        isOpen={!!errorModalMessage}
        buttonText={t(`${TP}.backToProduct`, 'Back to Product')}
        onSubmit={onSubmit}
      />
      {orderLoading && !order ? (
        // this prevents the page from showing the unauthorized
        // login panel while the browser is parsing and rerendering
        // the JS from getServerSideProps
        <Spinner threshold={0} showSpinner={true} />
      ) : (
        <Spinner threshold={0} showSpinner={orderLoading}>
          {checkoutPageBody}
        </Spinner>
      )}
    </>
  )
}

CheckoutPage.Layout = styled.div<{ $isMobile?: boolean }>`
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  justify-content: center;
  ${({ $isMobile }) =>
    !$isMobile &&
    css`
      flex-wrap: unset;
      height: calc(100vh - 60px);
      overflow-y: scroll;
    `};
`
CheckoutPage.Left = styled.div<{ $isMobile?: boolean }>`
  ${({ $isMobile }) =>
    $isMobile
      ? css`
          width: 100%;
        `
      : css`
          align-self: flex-start;
          padding-top: 20px;
          position: sticky;
          top: 0;
        `};
`
CheckoutPage.Right = styled.div<{ $isMobile?: boolean }>`
  ${({ $isMobile }) =>
    $isMobile
      ? css`
          width: 100%;
        `
      : css`
          padding-top: 20px;
        `};
`
CheckoutPage.OrderSummaryPanel = styled(OrderSummaryPanel)`
  ${media.large`
    width: 365px;
  `}
`
CheckoutPage.OrderSummaryGQL = styled(OrderSummaryPanelGQL)`
  ${media.large`
    width: 365px;
    position: sticky !important;
    top: 90px;
  `}
`
CheckoutPage.TempOrderContainer = styled.div`
  max-width: 500px;
`

const UnStickyOrderSummaryObserver = styled.div`
  height: 0px;
`

export default CheckoutPage
